import { apiFetch } from "../config/api";
import { getSession } from "../components/feature/auth/session";

// Authentication API Services
export const authService = {
  login: (login: string, password: string) =>
    apiFetch("/auth/login", {
      method: "POST",
      body: JSON.stringify({ login, password }),
    }),

  register: (userData: any) =>
    apiFetch("/auth/register", {
      method: "POST",
      body: JSON.stringify(userData),
    }),

  requestResetOtp: (identifier: string) =>
    apiFetch("/auth/forgot-password/request-otp", {
      method: "POST",
      body: JSON.stringify({ identifier }),
    }),

  verifyResetOtp: (email: string, otp: string) =>
    apiFetch("/auth/forgot-password/verify-otp", {
      method: "POST",
      body: JSON.stringify({ email, otp }),
    }),

  resetPassword: (email: string, otp: string, newPassword: string, confirmPassword?: string) =>
    apiFetch("/auth/forgot-password/reset-password", {
      method: "POST",
      body: JSON.stringify({ email, otp, newPassword, confirmPassword }),
    }),

  verifyMpin: (userId: string, mpin: string) =>
    apiFetch("/auth/verify-mpin", {
      method: "POST",
      body: JSON.stringify({ userId, mpin }),
    }),

  getMe: () => apiFetch("/auth/me"),

  forgotPassword: (data: { step: number; mobile?: string; email?: string; otp?: string; newPassword?: string }) =>
    apiFetch("/auth/forgot-password", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  logout: () =>
    apiFetch("/auth/logout", {
      method: "POST",
    }),
};

// KYC API Services
export const kycService = {
  getStatus: (retailerId: string) => apiFetch(`/retailer/kyc/status/${retailerId}`),

  saveDraft: (data: { retailerId: string; form: any; step?: number }) =>
    apiFetch("/retailer/kyc/draft", {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  uploadDocument: (data: {
    retailerId: string;
    docType: string;
    fileName: string;
    fileData: string;
    fileSize?: string;
  }) =>
    apiFetch("/retailer/kyc/documents", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  deleteDocument: (docId: string) =>
    apiFetch(`/retailer/kyc/documents/${docId}`, {
      method: "DELETE",
    }),

  submit: (kycData: any) =>
    apiFetch("/retailer/kyc/submit", {
      method: "POST",
      body: JSON.stringify(kycData),
    }),

  getAdminList: (
    params:
      | string
      | {
          status?: string;
          search?: string;
          businessType?: string;
          dateFrom?: string;
          dateTo?: string;
          sort?: string;
          page?: number;
          limit?: number;
        } = {}
  ) => {
    const opts = typeof params === "string" ? { status: params } : params;
    const query = new URLSearchParams();
    Object.entries(opts).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") query.append(key, String(value));
    });
    const qs = query.toString();
    return apiFetch(`/kyc/admin/list${qs ? `?${qs}` : ""}`);
  },

  approve: (kycId: string, remark?: string) =>
    apiFetch("/kyc/admin/approve", {
      method: "POST",
      body: JSON.stringify({ kycId, remark }),
    }),

  reject: (kycId: string, reason: string, remarks?: string) =>
    apiFetch("/kyc/admin/reject", {
      method: "POST",
      body: JSON.stringify({ kycId, reason, remarks }),
    }),

  refund: (kycId: string) =>
    apiFetch("/kyc/admin/refund", {
      method: "POST",
      body: JSON.stringify({ kycId }),
    }),
};

// Wallet API Services
export interface WalletTxnParams {
  category?: string;
  type?: string;
  search?: string;
  refNo?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  limit?: number;
}

function toQuery(params: Record<string, unknown>): string {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") query.append(key, String(value));
  });
  const qs = query.toString();
  return qs ? `?${qs}` : "";
}

export const walletService = {
  getRates: () => apiFetch("/wallet/rates"),
  getBalance: (retailerId?: string) => apiFetch(`/wallet/balance${retailerId ? `/${retailerId}` : ""}`),

  getTransactions: (retailerId: string, params: WalletTxnParams = {}) =>
    apiFetch(`/wallet/transactions/${retailerId}${toQuery(params as Record<string, unknown>)}`),

  getTopups: (retailerId: string, params: { status?: string; search?: string; page?: number; limit?: number } = {}) =>
    apiFetch(`/wallet/topups/${retailerId}${toQuery(params as Record<string, unknown>)}`),

  requestTopup: (topupData: {
    retailerId: string;
    amount: number;
    paymentMode: string;
    referenceNumber?: string;
    receiptFileName?: string;
  }) =>
    apiFetch("/wallet/topup", {
      method: "POST",
      body: JSON.stringify(topupData),
    }),

  getAdminSummary: () => apiFetch("/wallet/admin/summary"),

  getAdminTopups: (
    params: {
      status?: string;
      retailerId?: string;
      search?: string;
      dateFrom?: string;
      dateTo?: string;
      page?: number;
      limit?: number;
    } = {}
  ) => apiFetch(`/wallet/admin/topups${toQuery(params as Record<string, unknown>)}`),

  getAdminTransactions: (
    params: {
      retailerId?: string;
      type?: string;
      category?: string;
      search?: string;
      dateFrom?: string;
      dateTo?: string;
      page?: number;
      limit?: number;
    } = {}
  ) => apiFetch(`/wallet/admin/transactions${toQuery(params as Record<string, unknown>)}`),

  reviewTopup: (data: {
    requestId: string;
    action: "APPROVE" | "REJECT";
    remark?: string;
    amount?: number;
  }) =>
    apiFetch("/wallet/admin/topup-review", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  adjustBalance: (data: { retailerId: string; type: "CREDIT" | "DEBIT"; amount: number; reason?: string }) =>
    apiFetch("/wallet/admin/adjust", {
      method: "POST",
      body: JSON.stringify(data),
    }),
};

// Retailer dashboard & profile API Services
export const retailerService = {
  getDashboard: (retailerId: string) => apiFetch(`/retailer/dashboard/${retailerId}`),

  getNotifications: (retailerId: string) => apiFetch(`/retailer/notifications/${retailerId}`),

  getProfile: (retailerId: string) => apiFetch(`/retailer/profile/${retailerId}`),

  updateProfile: (retailerId: string, data: Record<string, unknown>) =>
    apiFetch(`/retailer/profile/${retailerId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  changePassword: (retailerId: string, data: { currentPassword: string; newPassword: string }) =>
    apiFetch(`/retailer/profile/${retailerId}/password`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  changeMpin: (retailerId: string, data: { currentMpin: string; newMpin: string }) =>
    apiFetch(`/retailer/profile/${retailerId}/mpin`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  verifyMpin: (retailerId: string, mpin: string) =>
    apiFetch(`/retailer/profile/${retailerId}/verify-mpin`, {
      method: "POST",
      body: JSON.stringify({ mpin }),
    }),
};

// Mandates API Services
export interface MandateListParams {
  retailerId?: string;
  status?: string;
  search?: string;
  bank?: string;
  authMode?: string;
  dateFrom?: string;
  dateTo?: string;
  sort?: string;
  page?: number;
  limit?: number;
}

export const mandateService = {
  create: (mandateData: any) =>
    apiFetch("/mandates/create", {
      method: "POST",
      body: JSON.stringify(mandateData),
    }),

  update: (id: string, mandateData: any) =>
    apiFetch(`/mandates/${id}`, {
      method: "PUT",
      body: JSON.stringify(mandateData),
    }),

  generateLink: (id: string, payload: { mpin: string; merchantToken?: string; deviceId?: string }) =>
    apiFetch(`/mandates/${id}/generate-link`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),


  getMandates: (params: MandateListParams = {}) => {
    const query = new URLSearchParams();
    if (params.retailerId) query.append("retailerId", params.retailerId);
    if (params.status) query.append("status", params.status);
    if (params.search) query.append("search", params.search);
    if (params.bank) query.append("bank", params.bank);
    if (params.authMode) query.append("authMode", params.authMode);
    if (params.dateFrom) query.append("dateFrom", params.dateFrom);
    if (params.dateTo) query.append("dateTo", params.dateTo);
    if (params.sort) query.append("sort", params.sort);
    if (params.page) query.append("page", String(params.page));
    if (params.limit) query.append("limit", String(params.limit));
    const qs = query.toString();
    return apiFetch(`/mandates${qs ? `?${qs}` : ""}`);
  },

  getById: (id: string, sync?: boolean) => apiFetch(`/mandates/${id}${sync ? "?sync=true" : ""}`),

  getRocketPayStatus: (id: string) => apiFetch(`/mandates/${id}/rocketpay`),

  syncRocketPay: (id: string) =>
    apiFetch(`/mandates/${id}/sync`, {
      method: "POST",
    }),

  cancel: (id: string, reason: string, remarks?: string) =>
    apiFetch(`/mandates/${id}/cancel`, {
      method: "POST",
      body: JSON.stringify({ reason, remarks }),
    }),

  delete: (id: string) =>
    apiFetch(`/mandates/${id}`, {
      method: "DELETE",
    }),

  simulateAuth: (refNo: string, outcome: "SUCCESS" | "DECLINED" | "FAILED") =>
    apiFetch("/mandates/auth-simulate", {
      method: "POST",
      body: JSON.stringify({ refNo, outcome }),
    }),

  syncWithRocketPay: (idOrRefNo: string) =>
    apiFetch(`/mandates/${idOrRefNo}/sync`, {
      method: "POST",
    }),

  sendAuthEmail: (idOrRefNo: string) =>
    apiFetch(`/mandates/${idOrRefNo}/send-email`, {
      method: "POST",
    }),
};

// Installments API Services
export interface InstallmentListParams {
  retailerId?: string;
  refNo?: string;
  status?: string;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  sort?: string;
  page?: number;
  limit?: number;
}

export const installmentService = {
  getInstallments: (params: InstallmentListParams = {}) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") query.append(key, String(value));
    });
    const qs = query.toString();
    return apiFetch(`/installments${qs ? `?${qs}` : ""}`);
  },

  getById: (id: string) => apiFetch(`/installments/${id}`),

  /** Get all installments for a specific mandate ID — calls RocketPay first, syncs to local DB */
  getByMandate: (mandateId: string, token?: string) =>
    apiFetch(`/installments/by-mandate/${encodeURIComponent(mandateId)}`, {
      headers: token ? { "x-token": token } : undefined,
    }),

  present: (id: string, outcome: "SUCCESS" | "FAILED", failureReason?: string) =>
    apiFetch(`/installments/${id}/present`, {
      method: "POST",
      body: JSON.stringify({ outcome, failureReason }),
    }),

  /**
   * Retry a failed installment.
   * schedule_date: YYYY-MM-DD format — date on which to schedule the retry (RocketPay spec).
   * Only allowed when installment is in COLLECTION_FAILED / FAILED state.
   */
  retry: (id: string, schedule_date?: string, retryDate?: string) =>
    apiFetch(`/installments/${id}/retry`, {
      method: "POST",
      body: JSON.stringify({ schedule_date, retryDate }),
    }),

  /**
   * Skip an installment.
   * Can only be done at least 2 days before the due date.
   */
  skip: (id: string, reason: string) =>
    apiFetch(`/installments/${id}/skip`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),

  /** Refresh installment status from RocketPay */
  refresh: (id: string) =>
    apiFetch(`/installments/${id}/refresh`, {
      method: "POST",
    }),

  markOffline: (
    id: string,
    offlineData: {
      amount: number;
      paymentMode: string;
      reference?: string;
      collectionDate?: string;
      remark?: string;
      receiptFileName?: string;
    }
  ) =>
    apiFetch(`/installments/${id}/offline`, {
      method: "POST",
      body: JSON.stringify(offlineData),
    }),

  /**
   * Bulk recon — calls RocketPay, syncs to local DB.
   * Max page_size: 100. Pass all IDs and paginate using page_number.
   */
  reconBulk: (
    payload: { page_number?: number; page_size?: number; ids: string[] },
    token?: string
  ) =>
    apiFetch("/installments/recon", {
      method: "POST",
      headers: token ? { "x-token": token } : undefined,
      body: JSON.stringify(payload),
    }),

  /**
   * Create an installment for an Adhoc schedule mandate.
   * Only valid when mandate has ADHOC frequency.
   */
  createForMandate: (
    mandateId: string,
    payload: { amount: number; due_date: string; reference_id?: string; description?: string },
    token?: string
  ) =>
    apiFetch(`/installments/create/${encodeURIComponent(mandateId)}`, {
      method: "POST",
      headers: token ? { "x-token": token } : undefined,
      body: JSON.stringify(payload),
    }),

  /**
   * Sync all active mandate installments directly from RocketPay V4 Rails
   */
  syncAll: () =>
    apiFetch("/installments/sync-all", {
      method: "POST",
    }),
};

// Admin API Services
export interface RetailerListParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  city?: string;
  sort?: string;
}

export const adminService = {
  getDashboard: (params: { range?: string; customFrom?: string; customTo?: string } = {}) => {
    const query = new URLSearchParams();
    if (params.range) query.append("range", params.range);
    if (params.customFrom) query.append("customFrom", params.customFrom);
    if (params.customTo) query.append("customTo", params.customTo);
    const qs = query.toString();
    return apiFetch(`/admin/dashboard${qs ? `?${qs}` : ""}`);
  },
  getRetailers: (params: RetailerListParams = {}) => {
    const query = new URLSearchParams();
    if (params.page) query.append("page", String(params.page));
    if (params.limit) query.append("limit", String(params.limit));
    if (params.search) query.append("search", params.search);
    if (params.status) query.append("status", params.status);
    if (params.city) query.append("city", params.city);
    if (params.sort) query.append("sort", params.sort);
    const qs = query.toString();
    return apiFetch(`/admin/retailers${qs ? `?${qs}` : ""}`);
  },
  getRetailerById: (id: string) => apiFetch(`/admin/retailers/${id}`),
  createRetailer: (retailerData: any) =>
    apiFetch("/admin/retailers", {
      method: "POST",
      body: JSON.stringify(retailerData),
    }),
  updateRetailer: (id: string, retailerData: any) =>
    apiFetch(`/admin/retailers/${id}`, {
      method: "PUT",
      body: JSON.stringify(retailerData),
    }),
  toggleRetailerStatus: (id: string, data: { isActive?: boolean; reason?: string; remarks?: string }) =>
    apiFetch(`/admin/retailers/${id}/status`, {
      method: "POST",
      body: JSON.stringify(data),
    }),
  resetRetailerPassword: (id: string, data: { password: string; notify?: boolean }) =>
    apiFetch(`/admin/retailers/${id}/reset-password`, {
      method: "POST",
      body: JSON.stringify(data),
    }),
  getLogs: () => apiFetch("/admin/logs"),
  getReports: (range?: string, customFrom?: string, customTo?: string) => {
    const query = new URLSearchParams();
    if (range) query.append("range", range);
    if (customFrom) query.append("customFrom", customFrom);
    if (customTo) query.append("customTo", customTo);
    const qs = query.toString();
    return apiFetch(`/admin/reports${qs ? `?${qs}` : ""}`);
  },
  getSettings: () => apiFetch("/admin/settings"),
  getEnterpriseSettings: () => apiFetch("/admin/settings/enterprise"),
  updateEnterpriseSettings: (enterprise: any) =>
    apiFetch("/admin/settings/enterprise", {
      method: "POST",
      body: JSON.stringify(enterprise),
    }),
  testEnterpriseConnection: (data?: any) =>
    apiFetch("/admin/settings/enterprise/test", {
      method: "POST",
      body: JSON.stringify(data || {}),
    }),
  updateGeneralSettings: (settings: any) =>
    apiFetch("/admin/settings/general", {
      method: "POST",
      body: JSON.stringify(settings),
    }),
  updateApiConfig: (config: any) =>
    apiFetch("/admin/settings/api", {
      method: "POST",
      body: JSON.stringify(config),
    }),
  testApiConnection: () =>
    apiFetch("/admin/settings/api/test", {
      method: "POST",
    }),
  updateNotificationSettings: (notifications: any) =>
    apiFetch("/admin/settings/notifications", {
      method: "POST",
      body: JSON.stringify(notifications),
    }),
  updateSecuritySettings: (security: any) =>
    apiFetch("/admin/settings/security", {
      method: "POST",
      body: JSON.stringify(security),
    }),
  updateBillingRates: (rates: {
    kycFee?: number;
    mandateCreationFee?: number;
    installmentCharge?: number;
    lowBalanceThreshold?: number;
    minTopup?: number;
    maxTopup?: number;
    requireReceipt?: boolean;
  }) =>
    apiFetch("/admin/settings/rates", {
      method: "POST",
      body: JSON.stringify(rates),
    }),
  getAdminUsers: () => apiFetch("/admin/users"),
  createAdminUser: (userData: { name: string; email: string; mobile?: string; role?: string; password?: string; active?: boolean; customPermissions?: any }) =>
    apiFetch("/admin/users", {
      method: "POST",
      body: JSON.stringify(userData),
    }),
  updateAdminUser: (id: string, userData: any) =>
    apiFetch(`/admin/users/${id}`, {
      method: "PUT",
      body: JSON.stringify(userData),
    }),
  toggleAdminUser: (id: string) =>
    apiFetch("/admin/users/toggle", {
      method: "POST",
      body: JSON.stringify({ id }),
    }),
  getRoleMatrix: () => apiFetch("/admin/rbac/matrix"),
  updateRoleMatrix: (matrix: any) =>
    apiFetch("/admin/rbac/matrix", {
      method: "PUT",
      body: JSON.stringify({ matrix }),
    }),
  resetRoleMatrix: () =>
    apiFetch("/admin/rbac/reset", {
      method: "POST",
    }),
  getUserRbac: (id: string) => apiFetch(`/admin/rbac/user/${id}`),
  updateUserRbac: (id: string, customPermissions: any) =>
    apiFetch(`/admin/rbac/user/${id}`, {
      method: "PUT",
      body: JSON.stringify({ customPermissions }),
    }),
};

// Helper for RocketPay V4 headers
function getRpHeaders(customToken?: string) {
  const token = customToken || getSession()?.merchantToken;
  const headers: Record<string, string> = {
    "x-app-context": "MERCHANT_API",
  };
  if (token) {
    headers["x-token"] = token;
  }
  return headers;
}

// RocketPay Mandate V4 Integration Services
export const rocketpayService = {
  getConfig: () => apiFetch("/rocketpay/v4/config"),

  // Mandate V4
  createMandateV4: (payload: any, token?: string) =>
    apiFetch("/rocketpay/v4/mandates", {
      method: "POST",
      headers: getRpHeaders(token),
      body: JSON.stringify(payload),
    }),

  getMandateV4: (id: string, token?: string) =>
    apiFetch(`/rocketpay/v4/mandates/${id}`, {
      method: "GET",
      headers: getRpHeaders(token),
    }),

  refreshMandateV4: (id: string, token?: string) =>
    apiFetch(`/rocketpay/v4/mandates/${id}/refresh`, {
      method: "POST",
      headers: getRpHeaders(token),
    }),

  deleteMandateV4: (id: string, token?: string) =>
    apiFetch(`/rocketpay/v4/mandates/${id}`, {
      method: "DELETE",
      headers: getRpHeaders(token),
    }),

  cancelMandateV4: (id: string, token?: string) =>
    apiFetch(`/rocketpay/v4/mandates/${id}/cancel`, {
      method: "POST",
      headers: getRpHeaders(token),
    }),

  reconMandatesV4: (payload: { page_number?: number; page_size?: number; ids: string[] }, token?: string) =>
    apiFetch("/rocketpay/v4/mandates/recon", {
      method: "POST",
      headers: getRpHeaders(token),
      body: JSON.stringify(payload),
    }),

  // Installments V4
  getInstallmentsV4: (mandateId: string, token?: string) =>
    apiFetch(`/rocketpay/v4/installments?mandate_id=${encodeURIComponent(mandateId)}`, {
      method: "GET",
      headers: getRpHeaders(token),
    }),

  getInstallmentByIdV4: (installmentId: string, token?: string) =>
    apiFetch(`/rocketpay/v4/installments/${installmentId}`, {
      method: "GET",
      headers: getRpHeaders(token),
    }),

  refreshInstallmentV4: (installmentId: string, token?: string) =>
    apiFetch(`/rocketpay/v4/installments/${installmentId}/refresh`, {
      method: "POST",
      headers: getRpHeaders(token),
    }),

  skipInstallmentV4: (installmentId: string, token?: string) =>
    apiFetch(`/rocketpay/v4/installments/${installmentId}/skip`, {
      method: "POST",
      headers: getRpHeaders(token),
    }),

  retryInstallmentV4: (installmentId: string, payload: { schedule_date: string }, token?: string) =>
    apiFetch(`/rocketpay/v4/installments/${installmentId}/retry`, {
      method: "POST",
      headers: getRpHeaders(token),
      body: JSON.stringify(payload),
    }),

  createInstallmentV4: (
    mandateId: string,
    payload: { amount: number; due_date: string; reference_id?: string; description?: string },
    token?: string
  ) =>
    apiFetch(`/rocketpay/v4/mandates/${mandateId}/installment`, {
      method: "POST",
      headers: getRpHeaders(token),
      body: JSON.stringify(payload),
    }),

  reconInstallmentsV4: (payload: { page_number?: number; page_size?: number; ids: string[] }, token?: string) =>
    apiFetch("/rocketpay/v4/installments/recon", {
      method: "POST",
      headers: getRpHeaders(token),
      body: JSON.stringify(payload),
    }),

  // Full Flow Demo Orchestration
  executeFlow: (payload: any) =>
    apiFetch("/rocketpay/flow/execute", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
};

